import apiClient from './client'

// Voice Gateway API endpoints (port 8082)

export interface TranscribeResponse {
  transcript: string
  intent?: string
  entities?: Record<string, string>
  confidence?: number
  language?: string
}

export interface IntentResponse {
  intent: string
  entities?: Record<string, string>
  confidence?: number
  suggestedAction?: string
}

export interface TTSResponse {
  audioUrl: string
  duration?: number
}

export interface ConversationMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  intent?: string
}

export interface ConversationResponse {
  response: string
  audioUrl?: string
  intent?: string
  suggestedActions?: string[]
  followUp?: string
}

export const voiceApi = {
  // Transcribe audio to text (supports Darija, Arabic, French)
  async transcribe(audioBlob: Blob, language?: string): Promise<TranscribeResponse> {
    const formData = new FormData()
    formData.append('audio', audioBlob, 'recording.webm')
    if (language) {
      formData.append('language', language)
    }

    const response = await apiClient.post<TranscribeResponse>('/api/voice/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Extract intent from text
  async extractIntent(text: string, language = 'fr'): Promise<IntentResponse> {
    const response = await apiClient.post<IntentResponse>('/api/voice/intent', {
      text,
      language,
    })
    return response.data
  },

  // Text to speech
  async textToSpeech(text: string, language = 'fr-FR', voice?: string): Promise<string> {
    const response = await apiClient.post<TTSResponse>('/api/voice/tts', {
      text,
      language,
      voice,
    })
    return response.data.audioUrl
  },

  // Conversational interaction
  async converse(
    message: string,
    context?: ConversationMessage[],
    language = 'fr'
  ): Promise<ConversationResponse> {
    const response = await apiClient.post<ConversationResponse>('/api/voice/converse', {
      message,
      context,
      language,
    })
    return response.data
  },

  // Stream audio for real-time transcription
  createTranscriptionStream(
    onTranscript: (text: string, isFinal: boolean) => void,
    onError: (error: string) => void,
    language = 'fr'
  ): {
    send: (audioChunk: Blob) => void
    close: () => void
  } {
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/voice/stream?language=${language}`
    const ws = new WebSocket(wsUrl)

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.transcript) {
          onTranscript(data.transcript, data.isFinal || false)
        }
        if (data.error) {
          onError(data.error)
        }
      } catch (err) {
        console.error('Failed to parse voice stream message:', err)
      }
    }

    ws.onerror = () => {
      onError('Connection error')
    }

    ws.onclose = () => {
      console.log('Voice stream closed')
    }

    return {
      send: (audioChunk: Blob) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(audioChunk)
        }
      },
      close: () => {
        ws.close()
      },
    }
  },

  // Get available voices for TTS
  async getVoices(): Promise<{ id: string; name: string; language: string }[]> {
    const response = await apiClient.get<{ voices: { id: string; name: string; language: string }[] }>('/api/voice/voices')
    return response.data.voices
  },

  // Get supported languages
  async getLanguages(): Promise<{ code: string; name: string; nativeName: string }[]> {
    const response = await apiClient.get<{ languages: { code: string; name: string; nativeName: string }[] }>('/api/voice/languages')
    return response.data.languages
  },
}

// Intent types for diabetes management
export const DIABETES_INTENTS = {
  LOG_MEAL: 'log_meal',
  LOG_GLUCOSE: 'log_glucose',
  LOG_ACTIVITY: 'log_activity',
  LOG_MEDICATION: 'log_medication',
  CHECK_GLUCOSE: 'check_glucose',
  CHECK_TREND: 'check_trend',
  GET_ADVICE: 'get_advice',
  SET_REMINDER: 'set_reminder',
  HELP: 'help',
  EMERGENCY: 'emergency',
} as const

// Entity types
export const ENTITY_TYPES = {
  FOOD: 'food',
  QUANTITY: 'quantity',
  GLUCOSE_VALUE: 'glucose_value',
  ACTIVITY_TYPE: 'activity_type',
  DURATION: 'duration',
  MEDICATION_NAME: 'medication_name',
  DOSAGE: 'dosage',
  TIME: 'time',
} as const
